import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Textarea } from "../components/ui/textarea";
import { Save, Download, ArrowLeft, Droplets, X, UserPlus, Copy, FileText, ChevronDown, ChevronUp, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// B/G groups: 3 sections — Red (deep) 10 rows, Yellow (medium) 10 rows, Green (shallow) 10 rows = 30
const EMPTY_ROWS_BG = () => [
  ...Array.from({ length: 10 }, (_, i) => ({ row_num: i + 1, buddy_a: "", buddy_b: "", buddy_c: "", section: "red" })),
  ...Array.from({ length: 10 }, (_, i) => ({ row_num: i + 11, buddy_a: "", buddy_b: "", buddy_c: "", section: "yellow" })),
  ...Array.from({ length: 10 }, (_, i) => ({ row_num: i + 21, buddy_a: "", buddy_b: "", buddy_c: "", section: "green" })),
];

// Mariners (B08-B11 / G08-G11) + Navigators (B12-B15 / G12-G15):
// expanded sheet — Red 14, Yellow 14, Green 20 = 48 rows
const EMPTY_ROWS_MARINER_NAV = () => [
  ...Array.from({ length: 14 }, (_, i) => ({ row_num: i + 1, buddy_a: "", buddy_b: "", buddy_c: "", section: "red" })),
  ...Array.from({ length: 14 }, (_, i) => ({ row_num: i + 15, buddy_a: "", buddy_b: "", buddy_c: "", section: "yellow" })),
  ...Array.from({ length: 20 }, (_, i) => ({ row_num: i + 29, buddy_a: "", buddy_b: "", buddy_c: "", section: "green" })),
];

// N groups: all kiddie pool — 20 rows
const EMPTY_ROWS_KIDDIE = () =>
  Array.from({ length: 20 }, (_, i) => ({ row_num: i + 1, buddy_a: "", buddy_b: "", buddy_c: "", section: "green" }));

// Swim color → CSS classes
function swimColorStyle(color) {
  if (!color) return { bg: "bg-gray-100", text: "text-gray-500", ring: "" };
  const c = color.toLowerCase().trim();
  if (c === "red / blue" || c === "red/blue")
    return { bg: "bg-gradient-to-r from-red-500 to-blue-500", text: "text-white", ring: "ring-purple-300" };
  if (c === "yellow / blue" || c === "yellow/blue")
    return { bg: "bg-gradient-to-r from-yellow-400 to-blue-500", text: "text-white", ring: "ring-teal-300" };
  if (c === "green / blue" || c === "green/blue")
    return { bg: "bg-gradient-to-r from-green-500 to-blue-500", text: "text-white", ring: "ring-cyan-300" };
  if (c === "red") return { bg: "bg-red-500", text: "text-white", ring: "ring-red-300" };
  if (c === "yellow") return { bg: "bg-yellow-400", text: "text-yellow-900", ring: "ring-yellow-300" };
  if (c === "blue") return { bg: "bg-blue-500", text: "text-white", ring: "ring-blue-300" };
  if (c === "green") return { bg: "bg-green-500", text: "text-white", ring: "ring-green-300" };
  return { bg: "bg-gray-200", text: "text-gray-600", ring: "" };
}

// Which sections a swim color is allowed in
function getAllowedSections(swimColor) {
  if (!swimColor) return ["red", "yellow", "green"]; // unknown = anywhere
  const c = swimColor.toLowerCase().trim();
  if (c === "red" || c === "blue" || c === "red / blue" || c === "red/blue")
    return ["red", "yellow", "green"]; // anywhere
  if (c === "yellow / blue" || c === "yellow/blue")
    return ["yellow", "green"];
  if (c === "yellow")
    return ["yellow", "green"];
  if (c === "green" || c === "green / blue" || c === "green/blue")
    return ["green"];
  return ["red", "yellow", "green"]; // default: anywhere
}

// Check if a row allows buddy_c (first row of each section = 3, rest = 2)
function allowsBuddyC(rowIdx, isNursery, isBG, isMarinerNav) {
  if (isNursery) {
    // N groups: all green, first line = 3, rest = 2
    return rowIdx === 0;
  }
  if (isMarinerNav) {
    // Mariners + Navigators (14/14/20): first line of each section.
    // Red: idx 0, Yellow: idx 14, Green: idx 28
    return rowIdx === 0 || rowIdx === 14 || rowIdx === 28;
  }
  if (isBG) {
    // Skippers B/G groups (10/10/10): first line of each section
    // Red: idx 0, Yellow: idx 10, Green: idx 20
    return rowIdx === 0 || rowIdx === 10 || rowIdx === 20;
  }
  return true; // fallback
}

export default function BuddyCheckSheet({ user, onBack }) {
  const groupCode = (user?.group_code || "").toUpperCase();
  const isNursery = groupCode.startsWith("N");
  const isBG = /^[BG]\d{1,2}$/.test(groupCode);
  // Mariners (B08-B11/G08-G11) + Navigators (B12-B15/G12-G15) get the
  // expanded 14/14/20 layout. Other B/G groups keep the legacy 10/10/10.
  const isMarinerNav = (() => {
    const m = groupCode.match(/^[BG](\d{1,2})$/);
    if (!m) return false;
    const n = parseInt(m[1], 10);
    return n >= 8 && n <= 15;
  })();

  const getEmptyRows = () => {
    if (isNursery) return EMPTY_ROWS_KIDDIE();
    if (isMarinerNav) return EMPTY_ROWS_MARINER_NAV();
    return EMPTY_ROWS_BG();
  };

  const [rows, setRows] = useState(getEmptyRows());
  const [campers, setCampers] = useState([]);
  const [comments, setComments] = useState("");
  const [period, setPeriod] = useState("AM");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [groupName, setGroupName] = useState(user?.group_name || "");
  const [groupNumber, setGroupNumber] = useState(user?.group_code || "");
  const [sheetId, setSheetId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const [selectedCamper, setSelectedCamper] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const [rosterDate, setRosterDate] = useState("");
  const [buddyTab, setBuddyTab] = useState("sheet");
  const [pastSheets, setPastSheets] = useState([]);
  const [pastSheetsLoading, setPastSheetsLoading] = useState(false);
  const [expandedSheet, setExpandedSheet] = useState(null);

  // Touch drag state
  const [touchDragging, setTouchDragging] = useState(null);
  const [copyingAMtoPM, setCopyingAMtoPM] = useState(false);
  const touchCloneRef = useRef(null);
  const touchTimerRef = useRef(null);
  const touchStartPos = useRef(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPastSheets = async () => {
    setPastSheetsLoading(true);
    try {
      const res = await axios.get(`${API}/buddy-sheets/archive`, { headers });
      setPastSheets(res.data || []);
    } catch { setPastSheets([]); }
    setPastSheetsLoading(false);
  };

  useEffect(() => { fetchPastSheets(); }, [groupCode]);

  // Lock state comes from the backend (admin toggle + the 9:20 AM / 5 PM
  // auto-lock scheduler). DO NOT rely on local device time — different devices
  // can drift and the admin can manually unlock at any moment.
  useEffect(() => {
    let alive = true;
    const checkLock = async () => {
      try {
        const res = await axios.get(`${API}/buddy-sheets/lock-status`, { headers });
        if (alive) setLocked(!!res.data?.locked);
      } catch {
        // Fall back to local-time guess if backend unreachable
        const now = new Date();
        const est = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
        const h = est.getHours();
        const m = est.getMinutes();
        if (alive) setLocked(h > 9 || (h === 9 && m >= 20));
      }
    };
    checkLock();
    const iv = setInterval(checkLock, 30000);
    return () => { alive = false; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const rosterParams = rosterDate ? `?date=${rosterDate}` : "";
      const [camperRes, sheetRes] = await Promise.all([
        axios.get(`${API}/roster/today${rosterParams}`, { headers }),
        axios.get(`${API}/buddy-sheets/today?period=${period}`, { headers }),
      ]);
      setCampers(camperRes.data?.campers || []);
      if (sheetRes.data && sheetRes.data.id) {
        const savedRows = sheetRes.data.rows || [];
        const template = getEmptyRows();
        const merged = template.map((tmpl) => {
          const saved = savedRows.find((r) => r.row_num === tmpl.row_num);
          return saved ? { ...tmpl, ...saved } : tmpl;
        });
        setRows(merged);
        setComments(sheetRes.data.comments || "");
        setSheetId(sheetRes.data.id);
        if (sheetRes.data.group_name) setGroupName(sheetRes.data.group_name);
        if (sheetRes.data.group_number) setGroupNumber(sheetRes.data.group_number);
      } else {
        // No saved sheet for this period — reset to blank
        setRows(getEmptyRows());
        setComments("");
        setSheetId(null);
      }
    } catch (e) { console.error(e); }
  }, [period, rosterDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const placedCampers = new Set();
  rows.forEach((r) => {
    if (r.buddy_a) placedCampers.add(r.buddy_a);
    if (r.buddy_b) placedCampers.add(r.buddy_b);
    if (r.buddy_c) placedCampers.add(r.buddy_c);
  });

  // ── Tweak 2: only one group-of-three on the whole sheet ─────────────
  // Once any row has buddy_c filled, that row owns "the three" and no
  // other row may offer a third slot. Cleared → all eligible rows can
  // show it again.
  const threeUsedRowIdx = rows.findIndex((r) => r.buddy_c);
  const threeIsUsed = threeUsedRowIdx >= 0;

  // ── Tweak 1: bucket unplaced campers by their PRIMARY swim color ────
  // Slash colors live with their base color (Red/Blue → Red, etc).
  // Nursery (N groups): everyone goes in the single green bucket.
  const _primaryColor = (sc) => {
    if (!sc) return "";
    const base = String(sc).toLowerCase().split("/")[0].trim();
    if (base.startsWith("red")) return "red";
    if (base.startsWith("yellow")) return "yellow";
    if (base.startsWith("green")) return "green";
    return "";
  };
  const campersByColor = { red: [], yellow: [], green: [], unassigned: [] };
  const sortedCampers = [...campers].sort((a, b) =>
    (a.last_name || "").localeCompare(b.last_name || "")
  );
  for (const c of sortedCampers) {
    const name = c.first_name + " " + c.last_name;
    if (placedCampers.has(name)) continue;
    if (isNursery) { campersByColor.green.push(c); continue; }
    const k = _primaryColor(c.swim_color);
    if (k) campersByColor[k].push(c);
    else campersByColor.unassigned.push(c);
  }

  const getDisplayName = (c) => c.first_name + " " + c.last_name;
  const getCamperByName = (name) => campers.find((c) => getDisplayName(c) === name);

  // Validate swim level placement
  const canPlaceInSection = (camperName, sectionColor) => {
    const camper = getCamperByName(camperName);
    if (!camper) return true; // unknown camper, allow
    const allowed = getAllowedSections(camper.swim_color);
    return allowed.includes(sectionColor);
  };

  // Place camper into slot with validation
  const placeCamper = (rowIdx, slot, camperName) => {
    if (locked || !camperName) return;
    if (rows[rowIdx][slot]) return; // occupied

    // Check buddy_c line limit
    if (slot === "buddy_c" && !allowsBuddyC(rowIdx, isNursery, isBG, isMarinerNav)) {
      toast.error("Only the first row of each section allows 3 buddies");
      return;
    }

    // ── Tweak 2: only ONE group-of-three across the whole sheet ──
    if (slot === "buddy_c" && threeIsUsed && threeUsedRowIdx !== rowIdx) {
      toast.error("Only one group of three allowed per sheet — clear the other first");
      return;
    }

    // Check swim level restriction
    const section = rows[rowIdx].section;
    if (!canPlaceInSection(camperName, section)) {
      const camper = getCamperByName(camperName);
      const allowed = getAllowedSections(camper?.swim_color).join(", ");
      toast.error(`${camperName} (${camper?.swim_color}) can only go in: ${allowed}`);
      return;
    }

    setRows((prev) =>
      prev.map((r, i) => (i === rowIdx ? { ...r, [slot]: camperName } : r))
    );
  };

  const clearSlot = (rowIdx, slot) => {
    if (locked) return;
    setRows((prev) =>
      prev.map((r, i) => (i === rowIdx ? { ...r, [slot]: "" } : r))
    );
  };

  // Tap-to-place
  const handleCamperTap = (name) => {
    if (locked || touchDragging) return;
    setSelectedCamper(selectedCamper === name ? null : name);
  };

  const handleSlotTap = (rowIdx, slot) => {
    if (locked) return;
    if (rows[rowIdx][slot]) {
      // Pick up camper from occupied slot for re-placement
      const name = rows[rowIdx][slot];
      setSelectedCamper(name);
      setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, [slot]: "" } : r));
      return;
    }
    if (selectedCamper) {
      placeCamper(rowIdx, slot, selectedCamper);
      setSelectedCamper(null);
    }
  };

  // Desktop HTML5 Drag
  const handleDragStart = (e, name, fromRow, fromSlot) => {
    const data = fromRow !== undefined ? JSON.stringify({ name, fromRow, fromSlot }) : name;
    e.dataTransfer.setData("text/plain", data);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = (e, rowIdx, slot) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("text/plain");
    let name, fromRow, fromSlot;
    try {
      const parsed = JSON.parse(raw);
      name = parsed.name;
      fromRow = parsed.fromRow;
      fromSlot = parsed.fromSlot;
    } catch {
      name = raw;
    }
    if (fromRow !== undefined && fromSlot !== undefined) {
      if (rowIdx === fromRow && slot === fromSlot) return;
      if (rows[rowIdx][slot]) return;
      if (slot === "buddy_c" && !allowsBuddyC(rowIdx, isNursery, isBG, isMarinerNav)) {
        toast.error("Only the first row of each section allows 3 buddies");
        return;
      }
      if (slot === "buddy_c" && threeIsUsed && threeUsedRowIdx !== rowIdx) {
        toast.error("Only one group of three allowed per sheet");
        return;
      }
      const section = rows[rowIdx].section;
      if (!canPlaceInSection(name, section)) {
        const camper = getCamperByName(name);
        const allowed = getAllowedSections(camper?.swim_color).join(", ");
        toast.error(`${name} (${camper?.swim_color}) can only go in: ${allowed}`);
        return;
      }
      setRows(prev => prev.map((r, i) => {
        let updated = { ...r };
        if (i === fromRow) updated[fromSlot] = "";
        if (i === rowIdx) updated[slot] = name;
        return updated;
      }));
    } else {
      placeCamper(rowIdx, slot, name);
    }
    setSelectedCamper(null);
  };

  // Touch Drag (mobile) — long-press
  const handleTouchStart = (e, name) => {
    if (locked || placedCampers.has(name)) return;
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };
    touchTimerRef.current = setTimeout(() => {
      setTouchDragging(name);
      setSelectedCamper(null);
      const clone = document.createElement("div");
      clone.className = "fixed z-[999] pointer-events-none px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-bold shadow-xl opacity-90";
      clone.textContent = name;
      clone.style.left = `${touch.clientX - 40}px`;
      clone.style.top = `${touch.clientY - 20}px`;
      document.body.appendChild(clone);
      touchCloneRef.current = clone;
    }, 500);
  };

  const handleTouchMoveCard = (e) => {
    if (touchTimerRef.current && !touchDragging) {
      const touch = e.touches[0];
      const dx = Math.abs(touch.clientX - (touchStartPos.current?.x || 0));
      const dy = Math.abs(touch.clientY - (touchStartPos.current?.y || 0));
      if (dx > 10 || dy > 10) {
        clearTimeout(touchTimerRef.current);
        touchTimerRef.current = null;
      }
    }
  };

  const handleTouchEndCard = () => {
    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
  };

  const handleGlobalTouchMove = useCallback((e) => {
    if (!touchDragging || !touchCloneRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    touchCloneRef.current.style.left = `${touch.clientX - 40}px`;
    touchCloneRef.current.style.top = `${touch.clientY - 20}px`;
    document.querySelectorAll("[data-drop-zone]").forEach((el) => el.classList.remove("ring-2", "ring-blue-500", "bg-blue-100"));
    const target = document.elementFromPoint(touch.clientX, touch.clientY);
    if (target) {
      const zone = target.closest("[data-drop-zone]");
      if (zone) zone.classList.add("ring-2", "ring-blue-500", "bg-blue-100");
    }
    const scrollMargin = 60;
    if (touch.clientY < scrollMargin) window.scrollBy(0, -8);
    else if (touch.clientY > window.innerHeight - scrollMargin) window.scrollBy(0, 8);
  }, [touchDragging]);

  const handleGlobalTouchEnd = useCallback((e) => {
    if (!touchDragging) return;
    const touch = e.changedTouches[0];
    document.querySelectorAll("[data-drop-zone]").forEach((el) => el.classList.remove("ring-2", "ring-blue-500", "bg-blue-100"));
    const target = document.elementFromPoint(touch.clientX, touch.clientY);
    if (target) {
      const zone = target.closest("[data-drop-zone]");
      if (zone) {
        const ri = parseInt(zone.dataset.rowIdx);
        const sl = zone.dataset.slot;
        if (!isNaN(ri) && sl) placeCamper(ri, sl, touchDragging);
      }
    }
    if (touchCloneRef.current) {
      document.body.removeChild(touchCloneRef.current);
      touchCloneRef.current = null;
    }
    setTouchDragging(null);
  }, [touchDragging]);

  useEffect(() => {
    if (touchDragging) {
      document.addEventListener("touchmove", handleGlobalTouchMove, { passive: false });
      document.addEventListener("touchend", handleGlobalTouchEnd);
      return () => {
        document.removeEventListener("touchmove", handleGlobalTouchMove);
        document.removeEventListener("touchend", handleGlobalTouchEnd);
      };
    }
  }, [touchDragging, handleGlobalTouchMove, handleGlobalTouchEnd]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/buddy-sheets`,
        { group_code: groupNumber, group_name: groupName, group_number: groupNumber, date, period, rows, comments },
        { headers }
      );
      setSheetId(res.data.id || sheetId);
      toast.success(`${period} buddy sheet saved!`);
      fetchPastSheets();
    } catch (e) { toast.error("Failed to save"); }
    setSaving(false);
  };

  const handleDuplicate = async () => {
    const targetPeriod = period === "AM" ? "PM" : "AM";
    setDuplicating(true);
    try {
      await axios.post(
        `${API}/buddy-sheets`,
        { group_code: groupNumber, group_name: groupName, group_number: groupNumber, date, period: targetPeriod, rows, comments },
        { headers }
      );
      toast.success(`Duplicated to ${targetPeriod}!`);
    } catch (e) { toast.error("Failed to duplicate"); }
    setDuplicating(false);
  };

  // ── Tweak 3: Copy AM → PM (prominent, with overwrite confirmation) ──
  const handleCopyAMtoPM = async () => {
    if (locked) return;
    if (period !== "AM") {
      toast.info("Switch to AM first, then copy → PM");
      return;
    }
    // Check if PM already has data
    try {
      const res = await axios.get(`${API}/buddy-sheets/today?period=PM`, { headers });
      const pmRows = res.data?.rows || [];
      const pmHasData = pmRows.some((r) => r.buddy_a || r.buddy_b || r.buddy_c);
      if (pmHasData) {
        const ok = window.confirm(
          "PM buddy sheet already has data. Overwrite with the current AM layout?"
        );
        if (!ok) return;
      }
    } catch { /* assume empty */ }
    setCopyingAMtoPM(true);
    try {
      // Save current AM first so the two stay in sync
      await axios.post(
        `${API}/buddy-sheets`,
        { group_code: groupNumber, group_name: groupName, group_number: groupNumber, date, period: "AM", rows, comments },
        { headers }
      );
      // Then write the same rows to PM
      await axios.post(
        `${API}/buddy-sheets`,
        { group_code: groupNumber, group_name: groupName, group_number: groupNumber, date, period: "PM", rows, comments },
        { headers }
      );
      toast.success("Copied AM → PM ✓");
      fetchPastSheets();
    } catch (e) { toast.error("Failed to copy AM → PM"); }
    setCopyingAMtoPM(false);
  };

  const handleDownloadPDF = async () => {
    if (!sheetId) { toast.error("Save first"); return; }
    try {
      const response = await fetch(`${API}/buddy-sheets/${sheetId}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error();
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Buddy_Sheet_${groupNumber}_${date}_${period}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("PDF downloaded!");
    } catch (e) { toast.error("Failed to download PDF"); }
  };

  // Section configs for B/G groups
  const bgSections = isMarinerNav
    ? [
        { key: "red", label: "RED — DEEP END", startIdx: 0, endIdx: 14, bgClass: "bg-red-600", textClass: "text-white" },
        { key: "yellow", label: "YELLOW — MEDIUM", startIdx: 14, endIdx: 28, bgClass: "bg-yellow-400", textClass: "text-yellow-900" },
        { key: "green", label: "GREEN — SHALLOW END", startIdx: 28, endIdx: 48, bgClass: "bg-green-600", textClass: "text-white" },
      ]
    : [
        { key: "red", label: "RED — DEEP END", startIdx: 0, endIdx: 10, bgClass: "bg-red-600", textClass: "text-white" },
        { key: "yellow", label: "YELLOW — MEDIUM", startIdx: 10, endIdx: 20, bgClass: "bg-yellow-400", textClass: "text-yellow-900" },
        { key: "green", label: "GREEN — SHALLOW END", startIdx: 20, endIdx: 30, bgClass: "bg-green-600", textClass: "text-white" },
      ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-amber-100">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-amber-800 text-white px-3 py-2 shadow-lg" data-testid="buddy-sheet-header">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onBack} className="text-white hover:bg-amber-700 h-8 px-2 gap-1" data-testid="buddy-back-btn">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs">Dashboard</span>
            </Button>
            <div>
              <h1 className="text-xs sm:text-sm font-bold tracking-wide">DAILY BUDDY CHECK SHEET</h1>
              <p className="text-[10px] text-amber-200 hidden sm:block">Rolling River Day Camp</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {locked && <Badge variant="destructive" className="text-[10px]">LOCKED</Badge>}
            {!locked && (
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-500 text-xs h-8" data-testid="buddy-save-btn">
                <Save className="w-3.5 h-3.5 sm:mr-1" />
                <span className="hidden sm:inline">{saving ? "Saving..." : "Save Sheet"}</span>
              </Button>
            )}
            {sheetId && (
              <>
                <Button size="sm" variant="outline" onClick={handleDuplicate} disabled={duplicating || locked} className="border-amber-400 text-amber-100 hover:bg-amber-700 text-xs h-8" data-testid="buddy-duplicate-btn">
                  <Copy className="w-3.5 h-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">{duplicating ? "..." : `Dup → ${period === "AM" ? "PM" : "AM"}`}</span>
                </Button>
                <Button size="sm" variant="outline" onClick={handleDownloadPDF} className="border-amber-400 text-amber-100 hover:bg-amber-700 text-xs h-8" data-testid="buddy-pdf-btn">
                  <Download className="w-3.5 h-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">PDF</span>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-3 pt-3">
        <div className="flex gap-2" data-testid="buddy-tabs">
          <button onClick={() => setBuddyTab("sheet")} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${buddyTab === "sheet" ? "bg-amber-600 text-white" : "bg-white text-gray-600 hover:bg-gray-100 border"}`} data-testid="buddy-tab-sheet">
            {sheetId ? "Edit Sheet" : "New Sheet"}
          </button>
          <button onClick={() => { setBuddyTab("past"); fetchPastSheets(); }} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-1.5 ${buddyTab === "past" ? "bg-amber-600 text-white" : "bg-white text-gray-600 hover:bg-gray-100 border"}`} data-testid="buddy-tab-past">
            <FileText className="w-3.5 h-3.5" /> Past Sheets {pastSheets.length > 0 && <Badge className="bg-amber-100 text-amber-800 text-[10px] px-1.5">{pastSheets.length}</Badge>}
          </button>
        </div>
      </div>

      {/* Past Sheets Tab */}
      {buddyTab === "past" && (
        <div className="max-w-5xl mx-auto px-3 py-3 space-y-3" data-testid="buddy-past-sheets">
          {pastSheetsLoading && <p className="text-sm text-muted-foreground text-center py-8">Loading sheets...</p>}
          {!pastSheetsLoading && pastSheets.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No buddy sheets filed yet for {groupCode}.</p>
            </div>
          )}
          {!pastSheetsLoading && pastSheets.map(s => {
            const isExpanded = expandedSheet === s.id;
            const filledRows = (s.rows || []).filter(r => r.buddy_a || r.buddy_b).length;
            return (
              <Card key={s.id} className="cursor-pointer transition-shadow hover:shadow-md border-amber-200" data-testid={`buddy-card-${s.id}`}>
                <CardContent className="p-0">
                  <button onClick={() => setExpandedSheet(isExpanded ? null : s.id)} className="w-full text-left p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="bg-amber-100 text-amber-800 text-[10px]">{s.period || "AM"}</Badge>
                          <Badge variant="outline" className="text-[10px]">{s.group_code}</Badge>
                          <span className="text-xs text-muted-foreground">{s.date}</span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">{s.period || "AM"} Buddy Sheet — {filledRows} pairs</p>
                        {s.comments && <p className="text-xs text-gray-500 mt-0.5 truncate">{s.comments}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <a href={`${API}/buddy-sheets/${s.id}/pdf`} onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-gray-600" data-testid={`buddy-pdf-${s.id}`}>
                          <Download className="w-4 h-4" />
                        </a>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="border-t px-4 pb-4 pt-3 bg-gray-50/50">
                      <table className="w-full text-xs">
                        <thead><tr className="text-gray-500"><th className="text-left py-1 pr-2">#</th><th className="text-left py-1 pr-2">Buddy A</th><th className="text-left py-1 pr-2">Buddy B</th><th className="text-left py-1">Section</th></tr></thead>
                        <tbody>
                          {(s.rows || []).filter(r => r.buddy_a || r.buddy_b).map((r, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="py-1 pr-2 text-gray-400">{r.row_num}</td>
                              <td className="py-1 pr-2 font-medium">{r.buddy_a || "—"}</td>
                              <td className="py-1 pr-2 font-medium">{r.buddy_b || "—"}</td>
                              <td className="py-1"><Badge className={`text-[9px] px-1 ${r.section === "red" ? "bg-red-100 text-red-700" : r.section === "yellow" ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"}`}>{r.section}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {s.comments && <p className="text-xs text-gray-500 mt-2 italic">Notes: {s.comments}</p>}
                      <div className="flex items-center gap-1 text-[10px] text-gray-400 mt-2">
                        <Clock className="w-3 h-3" /> Saved {s.saved_at ? new Date(s.saved_at).toLocaleString() : s.date}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Current Sheet Tab */}
      {buddyTab === "sheet" && (<>
      {/* Selected camper banner */}
      {selectedCamper && !touchDragging && (
        <div className="sticky top-[44px] z-10 bg-blue-600 text-white text-center py-2 text-xs font-medium shadow-md" data-testid="selected-camper-banner">
          <UserPlus className="w-3.5 h-3.5 inline mr-1" />
          Selected: <b>{selectedCamper}</b> — tap an empty slot to place
          <button onClick={() => setSelectedCamper(null)} className="ml-3 underline text-blue-200">Cancel</button>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-2 sm:px-3 py-3 space-y-3">
        {/* Form Info */}
        <Card className="bg-white/80 border-amber-300">
          <CardContent className="p-2.5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <label className="text-[10px] font-semibold text-amber-800 uppercase">Group Name</label>
                <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} disabled={locked} className="h-7 text-xs" data-testid="buddy-group-name" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-amber-800 uppercase">Group #</label>
                <Input value={groupNumber} onChange={(e) => setGroupNumber(e.target.value)} disabled={locked} className="h-7 text-xs" data-testid="buddy-group-number" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-amber-800 uppercase">Date</label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={locked} className="h-7 text-xs" data-testid="buddy-date" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-amber-800 uppercase">Period</label>
                <div className="flex gap-1 mt-0.5">
                  <Button size="sm" variant={period === "AM" ? "default" : "outline"} onClick={() => setPeriod("AM")} className={`flex-1 h-7 text-xs ${period === "AM" ? "bg-amber-700" : ""}`} data-testid="buddy-am-btn">AM</Button>
                  <Button size="sm" variant={period === "PM" ? "default" : "outline"} onClick={() => setPeriod("PM")} className={`flex-1 h-7 text-xs ${period === "PM" ? "bg-amber-700" : ""}`} data-testid="buddy-pm-btn">PM</Button>
                </div>
              </div>
            </div>
            <div className="mt-2 px-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button size="sm" onClick={handleSave} disabled={saving || locked} className="bg-green-600 hover:bg-green-500 disabled:bg-gray-400 h-9 text-xs font-bold" data-testid="buddy-save-period-btn">
                <Save className="w-3.5 h-3.5 mr-1" />
                {saving ? "Saving..." : `Save ${period}`}
              </Button>
              <Button
                size="sm"
                onClick={handleCopyAMtoPM}
                disabled={copyingAMtoPM || locked || period !== "AM"}
                className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-300 h-9 text-xs font-bold text-white"
                data-testid="buddy-copy-am-to-pm-btn"
                title={period !== "AM" ? "Switch to AM first to copy → PM" : "Copy current AM layout to PM"}
              >
                <Copy className="w-3.5 h-3.5 mr-1" />
                {copyingAMtoPM ? "Copying..." : "Copy AM → PM"}
              </Button>
            </div>
            {/* Roster date picker for testing */}
            <div className="mt-2 flex items-center gap-2 px-1">
              <label className="text-[10px] font-semibold text-amber-800 uppercase whitespace-nowrap">Roster Date:</label>
              <input
                type="date"
                min="2026-06-29"
                max="2026-08-20"
                value={rosterDate}
                onChange={(e) => setRosterDate(e.target.value)}
                className="px-2 py-1 text-xs border border-amber-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 h-7"
                data-testid="buddy-roster-date"
              />
              <span className="text-[10px] text-amber-600">{campers.length} camper{campers.length !== 1 ? "s" : ""} on roster</span>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <p className="text-center text-[10px] sm:text-xs text-amber-700 italic px-1">
          {isNursery ? (
            <>Tap a camper to select, then tap an empty slot. Hold to drag. All rows = <b>Kiddie Pool (Green)</b>. First row: 3 buddies, rest: 2 buddies.</>
          ) : (
            <>Tap a camper to select, then tap an empty slot. Hold to drag. <b>Red</b> = Deep, <b>Yellow</b> = Medium, <b>Green</b> = Shallow. First row per section: 3 buddies, rest: 2.</>
          )}
        </p>

        {isNursery ? (
          /* ══ KIDDIE POOL Section (N groups) ══ */
          <div className="space-y-0">
            <div className="bg-green-600 text-white text-center py-1.5 rounded-t-lg font-bold text-xs tracking-widest">
              KIDDIE POOL (Rows 1–20)
            </div>
            <Card className="bg-white/90 border-amber-300 rounded-t-none overflow-hidden">
              <BuddyTable
                rows={rows}
                startIdx={0}
                endIdx={20}
                sectionStartIdx={0}
                locked={locked}
                selectedCamper={selectedCamper}
                touchDragging={touchDragging}
                onSlotTap={handleSlotTap}
                onDrop={handleDrop}
                onClear={clearSlot}
                onDragStart={handleDragStart}
                getCamperByName={getCamperByName}
                isNursery={isNursery}
                isBG={false}
                threeIsUsed={threeIsUsed}
                threeUsedRowIdx={threeUsedRowIdx}
              />
            </Card>
            {/* Unplaced campers tray — directly under the kiddie pool */}
            <UnplacedTray
              colorKey="green"
              label="Unplaced — drop above"
              campers={campersByColor.green}
              isNursery
              locked={locked}
              selectedCamper={selectedCamper}
              touchDragging={touchDragging}
              onTap={handleCamperTap}
              onDragStart={handleDragStart}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMoveCard}
              onTouchEnd={handleTouchEndCard}
              getDisplayName={getDisplayName}
            />
          </div>
        ) : (
          /* ══ B/G Groups: Red / Yellow / Green ══ */
          <div className="space-y-3">
            {bgSections.map((sec) => (
              <div key={sec.key} className="space-y-0">
                <div className={`${sec.bgClass} ${sec.textClass} text-center py-1.5 rounded-t-lg font-bold text-xs tracking-widest`}>
                  {sec.label} (Rows {sec.startIdx + 1}–{sec.endIdx})
                </div>
                <Card className="bg-white/90 border-amber-300 rounded-t-none overflow-hidden">
                  <BuddyTable
                    rows={rows}
                    startIdx={sec.startIdx}
                    endIdx={sec.endIdx}
                    sectionStartIdx={sec.startIdx}
                    locked={locked}
                    selectedCamper={selectedCamper}
                    touchDragging={touchDragging}
                    onSlotTap={handleSlotTap}
                    onDrop={handleDrop}
                    onClear={clearSlot}
                    onDragStart={handleDragStart}
                    getCamperByName={getCamperByName}
                    isNursery={false}
                    isBG={true}
                    sectionColor={sec.key}
                    threeIsUsed={threeIsUsed}
                    threeUsedRowIdx={threeUsedRowIdx}
                  />
                </Card>
                {/* Unplaced campers for THIS color — bucket directly below */}
                <UnplacedTray
                  colorKey={sec.key}
                  label={`Unplaced ${sec.key.toUpperCase()} swimmers — drag/tap to place above`}
                  campers={campersByColor[sec.key]}
                  locked={locked}
                  selectedCamper={selectedCamper}
                  touchDragging={touchDragging}
                  onTap={handleCamperTap}
                  onDragStart={handleDragStart}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMoveCard}
                  onTouchEnd={handleTouchEndCard}
                  getDisplayName={getDisplayName}
                />
              </div>
            ))}
            {/* Campers with no/unknown swim color — show as a fallback bucket */}
            {campersByColor.unassigned.length > 0 && (
              <div className="space-y-0">
                <div className="bg-gray-500 text-white text-center py-1.5 rounded-t-lg font-bold text-xs tracking-widest">
                  NO SWIM COLOR ON FILE — Place anywhere allowed
                </div>
                <UnplacedTray
                  colorKey="unassigned"
                  label="No swim color set"
                  campers={campersByColor.unassigned}
                  locked={locked}
                  selectedCamper={selectedCamper}
                  touchDragging={touchDragging}
                  onTap={handleCamperTap}
                  onDragStart={handleDragStart}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMoveCard}
                  onTouchEnd={handleTouchEndCard}
                  getDisplayName={getDisplayName}
                  forceShowEmpty
                />
              </div>
            )}
          </div>
        )}

        {/* Comments */}
        <Card className="bg-white/80 border-amber-300">
          <CardContent className="p-2.5">
            <label className="text-[10px] font-semibold text-amber-800 uppercase">Comments</label>
            <Textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              disabled={locked}
              placeholder="Add any comments here..."
              className="mt-1 text-xs min-h-[50px]"
              data-testid="buddy-comments"
            />
          </CardContent>
        </Card>

        {/* Camper Pool — all campers (collapsible, fallback view) */}
        <details className="bg-white/90 border border-amber-300 rounded-lg">
          <summary className="px-3 py-2 cursor-pointer text-xs font-bold text-amber-800 flex items-center gap-2">
            <Droplets className="w-3.5 h-3.5" />
            Full roster — {campers.length - placedCampers.size} of {campers.length} remaining
            <span className="text-[10px] text-amber-500 font-normal ml-auto">(click to expand)</span>
          </summary>
          <div className="p-2.5 pt-0">
            <ScrollArea className="max-h-[40vh] overflow-auto">
              <div className="flex flex-wrap gap-1.5" data-testid="camper-pool">
                {campers
                  .sort((a, b) => a.last_name.localeCompare(b.last_name))
                  .map((c) => {
                    const name = getDisplayName(c);
                    const isPlaced = placedCampers.has(name);
                    const isSelected = selectedCamper === name;
                    const isDragging = touchDragging === name;
                    const sColor = isNursery
                      ? { bg: "bg-green-500", text: "text-white", ring: "ring-green-300" }
                      : swimColorStyle(c.swim_color);
                    const allowed = isNursery ? ["green"] : getAllowedSections(c.swim_color);
                    const sectionLabels = allowed.map(s => s === "red" ? "R" : s === "yellow" ? "Y" : "G").join("/");
                    return (
                      <div
                        key={c.id}
                        draggable={!locked && !isPlaced}
                        onDragStart={(e) => handleDragStart(e, name)}
                        onTouchStart={(e) => !isPlaced && handleTouchStart(e, name)}
                        onTouchMove={handleTouchMoveCard}
                        onTouchEnd={handleTouchEndCard}
                        onClick={() => !isPlaced && handleCamperTap(name)}
                        className={`flex items-center gap-1.5 px-2 py-2 rounded-lg border text-[11px] sm:text-xs font-medium transition-all select-none ${
                          isPlaced
                            ? "bg-gray-100 text-gray-400 border-gray-200 opacity-40 line-through"
                            : isDragging
                            ? "opacity-30 scale-95"
                            : isSelected
                            ? "bg-blue-500 text-white border-blue-600 ring-2 ring-blue-300 shadow-lg scale-105"
                            : "bg-white border-amber-200 hover:border-amber-400 hover:shadow cursor-grab active:cursor-grabbing"
                        }`}
                        data-testid={`pool-camper-${c.id}`}
                      >
                        <span>{name}</span>
                        {isNursery ? (
                          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold ring-1 bg-green-500 text-white ring-green-300">
                            Kiddie
                          </span>
                        ) : c.swim_color ? (
                          <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ring-1 ${sColor.bg} ${sColor.text} ${sColor.ring}`}>
                            {c.swim_color.replace(" / ", "/")}
                          </span>
                        ) : null}
                        {!isNursery && !isPlaced && (
                          <span className="text-[8px] text-gray-400">{sectionLabels}</span>
                        )}
                      </div>
                    );
                  })}
              </div>
            </ScrollArea>
          </div>
        </details>
        <div className="h-20" />
      </div>
      </>)}
    </div>
  );
}

// ─── Unplaced campers tray (one per swim color, sits directly under each section) ───
function UnplacedTray({
  colorKey,
  label,
  campers,
  isNursery,
  locked,
  selectedCamper,
  touchDragging,
  onTap,
  onDragStart,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  getDisplayName,
  forceShowEmpty,
}) {
  if (!campers || campers.length === 0) {
    if (!forceShowEmpty) return null;
    return (
      <div className="bg-white/70 border border-t-0 border-amber-200 rounded-b-lg p-2 text-center text-[10px] text-gray-400 italic">
        All {colorKey} campers placed ✓
      </div>
    );
  }
  const dot = isNursery || colorKey === "green"
    ? "bg-green-500 ring-green-300"
    : colorKey === "red"
    ? "bg-red-500 ring-red-300"
    : colorKey === "yellow"
    ? "bg-yellow-400 ring-yellow-300"
    : "bg-gray-400 ring-gray-300";
  return (
    <div className="bg-white/85 border border-t-0 border-amber-200 rounded-b-lg p-2">
      <div className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide mb-1.5 flex items-center gap-1">
        <span className={`w-2 h-2 rounded-full ring-1 ${dot}`} />
        {label} <span className="ml-auto text-amber-500 font-normal">{campers.length} left</span>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid={`unplaced-tray-${colorKey}`}>
        {campers.map((c) => {
          const name = getDisplayName(c);
          const isSelected = selectedCamper === name;
          const isDragging = touchDragging === name;
          return (
            <div
              key={c.id}
              draggable={!locked}
              onDragStart={(e) => onDragStart(e, name)}
              onTouchStart={(e) => onTouchStart(e, name)}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onClick={() => onTap(name)}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border text-[11px] sm:text-xs font-medium transition-all select-none ${
                isDragging
                  ? "opacity-30 scale-95"
                  : isSelected
                  ? "bg-blue-500 text-white border-blue-600 ring-2 ring-blue-300 shadow-lg scale-105"
                  : "bg-white border-amber-300 hover:border-amber-500 hover:shadow cursor-grab active:cursor-grabbing"
              }`}
              data-testid={`unplaced-camper-${c.id}`}
            >
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot.split(" ")[0]}`} />
              <span>{name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Buddy Row Table Component ───
function BuddyTable({ rows, startIdx, endIdx, sectionStartIdx, locked, selectedCamper, touchDragging, onSlotTap, onDrop, onClear, onDragStart, getCamperByName, isNursery, isBG, sectionColor, threeIsUsed, threeUsedRowIdx }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={`buddy-table-${startIdx}`}>
        <thead>
          <tr className="bg-amber-800 text-white text-[10px] sm:text-xs">
            <th className="w-8 p-1.5 text-center">#</th>
            <th className="p-1.5 text-left">Buddy A</th>
            <th className="p-1.5 text-left">Buddy B</th>
            <th className="p-1.5 text-left">Buddy C</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(startIdx, endIdx).map((row, relIdx) => {
            const idx = startIdx + relIdx;
            const isFirstRow = relIdx === 0;
            // Only first row of each section gets buddy_c — and only if no other
            // row across the whole sheet already owns the single group of three.
            const showC = isFirstRow && (!threeIsUsed || threeUsedRowIdx === idx);

            return (
              <tr key={idx} className={relIdx % 2 === 0 ? "bg-amber-50" : "bg-amber-100/50"} data-testid={`buddy-row-${idx}`}>
                <td className="p-1 text-center font-bold text-amber-800 text-xs">{row.row_num}</td>
                {["buddy_a", "buddy_b"].map((slot) => {
                  const camperInSlot = row[slot] ? getCamperByName(row[slot]) : null;
                  return (
                    <td
                      key={slot}
                      className="p-1"
                      onClick={() => onSlotTap(idx, slot)}
                      onDragOver={(e) => { if (!row[slot]) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
                      onDrop={(e) => onDrop(e, idx, slot)}
                      data-testid={`buddy-cell-${idx}-${slot}`}
                    >
                      {row[slot] ? (
                        <div
                          draggable={!locked}
                          onDragStart={(e) => onDragStart(e, row[slot], idx, slot)}
                          className="flex items-center gap-1 bg-white rounded px-1.5 py-1.5 border border-amber-200 text-[11px] sm:text-xs min-h-[32px] cursor-grab active:cursor-grabbing"
                        >
                          {camperInSlot?.swim_color && (
                            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${swimColorStyle(camperInSlot.swim_color).bg}`} />
                          )}
                          <span className="flex-1 truncate font-medium">{row[slot]}</span>
                          {!locked && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onClear(idx, slot); }}
                              className="text-red-400 hover:text-red-600 p-0.5 flex-shrink-0"
                              data-testid={`buddy-clear-${idx}-${slot}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div
                          data-drop-zone=""
                          data-row-idx={idx}
                          data-slot={slot}
                          className={`min-h-[32px] rounded border-2 border-dashed flex items-center justify-center text-[10px] transition-colors ${
                            selectedCamper || touchDragging
                              ? "border-blue-400 bg-blue-50 text-blue-500 font-medium"
                              : "border-amber-200 text-amber-300"
                          }`}
                        >
                          {selectedCamper || touchDragging ? "Drop here" : "—"}
                        </div>
                      )}
                    </td>
                  );
                })}
                {/* Buddy C — only on first row of section */}
                <td className="p-1">
                  {showC ? (
                    (() => {
                      const slot = "buddy_c";
                      const camperInSlot = row[slot] ? getCamperByName(row[slot]) : null;
                      return row[slot] ? (
                        <div
                          draggable={!locked}
                          onDragStart={(e) => onDragStart(e, row[slot], idx, "buddy_c")}
                          onClick={() => onSlotTap(idx, "buddy_c")}
                          className="flex items-center gap-1 bg-white rounded px-1.5 py-1.5 border border-amber-200 text-[11px] sm:text-xs min-h-[32px] cursor-grab active:cursor-grabbing"
                        >
                          {camperInSlot?.swim_color && (
                            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${swimColorStyle(camperInSlot.swim_color).bg}`} />
                          )}
                          <span className="flex-1 truncate font-medium">{row[slot]}</span>
                          {!locked && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onClear(idx, slot); }}
                              className="text-red-400 hover:text-red-600 p-0.5 flex-shrink-0"
                              data-testid={`buddy-clear-${idx}-${slot}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div
                          data-drop-zone=""
                          data-row-idx={idx}
                          data-slot={slot}
                          onClick={() => onSlotTap(idx, slot)}
                          onDragOver={(e) => { if (!row[slot]) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
                          onDrop={(e) => onDrop(e, idx, slot)}
                          data-testid={`buddy-cell-${idx}-${slot}`}
                          className={`min-h-[32px] rounded border-2 border-dashed flex items-center justify-center text-[10px] transition-colors ${
                            selectedCamper || touchDragging
                              ? "border-blue-400 bg-blue-50 text-blue-500 font-medium"
                              : "border-amber-200 text-amber-300"
                          }`}
                        >
                          {selectedCamper || touchDragging ? "Drop here" : "—"}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="min-h-[32px] rounded bg-gray-100 flex items-center justify-center text-[10px] text-gray-300">
                      —
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
